import _objectSpread from "/codebuild/output/src455542086/src/node_modules/babel-preset-react-app/node_modules/@babel/runtime/helpers/esm/objectSpread2";
import _createForOfIteratorHelper from "/codebuild/output/src455542086/src/node_modules/babel-preset-react-app/node_modules/@babel/runtime/helpers/esm/createForOfIteratorHelper";
import _classCallCheck from "/codebuild/output/src455542086/src/node_modules/babel-preset-react-app/node_modules/@babel/runtime/helpers/esm/classCallCheck";
import _createClass from "/codebuild/output/src455542086/src/node_modules/babel-preset-react-app/node_modules/@babel/runtime/helpers/esm/createClass";
import _inherits from "/codebuild/output/src455542086/src/node_modules/babel-preset-react-app/node_modules/@babel/runtime/helpers/esm/inherits";
import _createSuper from "/codebuild/output/src455542086/src/node_modules/babel-preset-react-app/node_modules/@babel/runtime/helpers/esm/createSuper";
/**
 * @license Copyright (c) 2003-2024, CKSource Holding sp. z o.o. All rights reserved.
 * For licensing, see LICENSE.md or https://ckeditor.com/legal/ckeditor-oss-license
 */
/**
 * @module image/autoimage
 */
import { Plugin } from 'ckeditor5/src/core.js';
import { Clipboard } from 'ckeditor5/src/clipboard.js';
import { LivePosition, LiveRange } from 'ckeditor5/src/engine.js';
import { Undo } from 'ckeditor5/src/undo.js';
import { Delete } from 'ckeditor5/src/typing.js';
import { global } from 'ckeditor5/src/utils.js';
import ImageUtils from './imageutils.js';
// Implements the pattern: http(s)://(www.)example.com/path/to/resource.ext?query=params&maybe=too.
var IMAGE_URL_REGEXP = new RegExp(String(/^(http(s)?:\/\/)?[\w-]+\.[\w.~:/[\]@!$&'()*+,;=%-]+/.source + /\.(jpg|jpeg|png|gif|ico|webp|JPG|JPEG|PNG|GIF|ICO|WEBP)/.source + /(\?[\w.~:/[\]@!$&'()*+,;=%-]*)?/.source + /(#[\w.~:/[\]@!$&'()*+,;=%-]*)?$/.source));
/**
 * The auto-image plugin. It recognizes image links in the pasted content and embeds
 * them shortly after they are injected into the document.
 */
var AutoImage = /*#__PURE__*/function (_Plugin) {
  _inherits(AutoImage, _Plugin);
  var _super = _createSuper(AutoImage);
  /**
   * @inheritDoc
   */
  function AutoImage(editor) {
    var _this;
    _classCallCheck(this, AutoImage);
    _this = _super.call(this, editor);
    _this._timeoutId = null;
    _this._positionToInsert = null;
    return _this;
  }
  /**
   * @inheritDoc
   */
  _createClass(AutoImage, [{
    key: "init",
    value: function init() {
      var _this2 = this;
      var editor = this.editor;
      var modelDocument = editor.model.document;
      var clipboardPipeline = editor.plugins.get('ClipboardPipeline');
      // We need to listen on `Clipboard#inputTransformation` because we need to save positions of selection.
      // After pasting, the content between those positions will be checked for a URL that could be transformed
      // into an image.
      this.listenTo(clipboardPipeline, 'inputTransformation', function () {
        var firstRange = modelDocument.selection.getFirstRange();
        var leftLivePosition = LivePosition.fromPosition(firstRange.start);
        leftLivePosition.stickiness = 'toPrevious';
        var rightLivePosition = LivePosition.fromPosition(firstRange.end);
        rightLivePosition.stickiness = 'toNext';
        modelDocument.once('change:data', function () {
          _this2._embedImageBetweenPositions(leftLivePosition, rightLivePosition);
          leftLivePosition.detach();
          rightLivePosition.detach();
        }, {
          priority: 'high'
        });
      });
      editor.commands.get('undo').on('execute', function () {
        if (_this2._timeoutId) {
          global.window.clearTimeout(_this2._timeoutId);
          _this2._positionToInsert.detach();
          _this2._timeoutId = null;
          _this2._positionToInsert = null;
        }
      }, {
        priority: 'high'
      });
    }
    /**
     * Analyzes the part of the document between provided positions in search for a URL representing an image.
     * When the URL is found, it is automatically converted into an image.
     *
     * @param leftPosition Left position of the selection.
     * @param rightPosition Right position of the selection.
     */
  }, {
    key: "_embedImageBetweenPositions",
    value: function _embedImageBetweenPositions(leftPosition, rightPosition) {
      var _this3 = this;
      var editor = this.editor;
      // TODO: Use a marker instead of LiveRange & LivePositions.
      var urlRange = new LiveRange(leftPosition, rightPosition);
      var walker = urlRange.getWalker({
        ignoreElementEnd: true
      });
      var selectionAttributes = Object.fromEntries(editor.model.document.selection.getAttributes());
      var imageUtils = this.editor.plugins.get('ImageUtils');
      var src = '';
      var _iterator = _createForOfIteratorHelper(walker),
        _step;
      try {
        for (_iterator.s(); !(_step = _iterator.n()).done;) {
          var node = _step.value;
          if (node.item.is('$textProxy')) {
            src += node.item.data;
          }
        }
      } catch (err) {
        _iterator.e(err);
      } finally {
        _iterator.f();
      }
      src = src.trim();
      // If the URL does not match the image URL regexp, let's skip that.
      if (!src.match(IMAGE_URL_REGEXP)) {
        urlRange.detach();
        return;
      }
      // Position will not be available in the `setTimeout` function so let's clone it.
      this._positionToInsert = LivePosition.fromPosition(leftPosition);
      // This action mustn't be executed if undo was called between pasting and auto-embedding.
      this._timeoutId = setTimeout(function () {
        // Do nothing if image element cannot be inserted at the current position.
        // See https://github.com/ckeditor/ckeditor5/issues/2763.
        // Condition must be checked after timeout - pasting may take place on an element, replacing it. The final position matters.
        var imageCommand = editor.commands.get('insertImage');
        if (!imageCommand.isEnabled) {
          urlRange.detach();
          return;
        }
        editor.model.change(function (writer) {
          _this3._timeoutId = null;
          writer.remove(urlRange);
          urlRange.detach();
          var insertionPosition;
          // Check if the position where the element should be inserted is still valid.
          // Otherwise leave it as undefined to use the logic of insertImage().
          if (_this3._positionToInsert.root.rootName !== '$graveyard') {
            insertionPosition = _this3._positionToInsert.toPosition();
          }
          imageUtils.insertImage(_objectSpread(_objectSpread({}, selectionAttributes), {}, {
            src: src
          }), insertionPosition);
          _this3._positionToInsert.detach();
          _this3._positionToInsert = null;
        });
        var deletePlugin = editor.plugins.get('Delete');
        deletePlugin.requestUndoOnBackspace();
      }, 100);
    }
  }], [{
    key: "requires",
    get:
    /**
     * @inheritDoc
     */
    function get() {
      return [Clipboard, ImageUtils, Undo, Delete];
    }
    /**
     * @inheritDoc
     */
  }, {
    key: "pluginName",
    get: function get() {
      return 'AutoImage';
    }
  }]);
  return AutoImage;
}(Plugin);
export { AutoImage as default };